export const CASE_PHASE = {
  DRI_PRELIMINAR: 'dri_preliminar',
  DRI_FINAL: 'dri_final',
  PROSPECTO: 'prospecto',
  BORRADOR_A: 'borrador_a',
  BORRADOR_B: 'borrador_b',
  VERSION_FINAL_EDITADA: 'version_final_editada',
  PUBLICADO: 'publicado'
};

const PROSPECTO_SENTINEL_FIELDS = {
  technology: ['commercial_name', 'contacts_comercialization', 'intellectual_property', 'opinions'],
  alternative: ['contacts', 'patents', 'opinions'],
  experience: ['testimonies', 'collaboration', 'general_adaptation'],
  innovation: ['patents_wrights', 'components', 'scientific_aspects'],
  assistance: ['opinions', 'rights_property', 'access_requirements'],
  financial: ['opinions', 'financial_products', 'costs'],
  // theme, topic y vision los escribe el Pre. El Prospecto empieza cuando
  // la matriz del Gestor deja código de caso (columna 3.code) o versión corta.
  vision: ['code', 'vision_short'],
  regulation: ['legal_instrument', 'issuer_organ', 'major_mandate']
};

const PHASE_LABELS = {
  dri_preliminar: {
    key: 'flow.case-phase.dri-preliminar',
    fallback: 'DRI 1',
    tone: 'warning'
  },
  dri_final: {
    key: 'flow.case-phase.dri-final',
    fallback: 'DRI 2',
    tone: 'info'
  },
  prospecto: {
    key: 'flow.case-phase.prospecto',
    fallback: 'Prospecto 1',
    tone: 'primary'
  },
  borrador_a: {
    key: 'flow.case-phase.borrador-a',
    fallback: 'Borrador del Caso A',
    tone: 'secondary'
  },
  borrador_b: {
    key: 'flow.case-phase.borrador-b',
    fallback: 'Borrador del Caso B',
    tone: 'secondary'
  },
  version_final_editada: {
    key: 'flow.case-phase.version-final-editada',
    fallback: 'Versión Final Editada',
    tone: 'success'
  },
  publicado: {
    key: 'flow.case-phase.publicado',
    fallback: 'Publicado',
    tone: 'success'
  }
};

const hasText = (value) => value != null && String(value).trim().length > 0;

const normalizeRoleKey = (roleName = '') => {
  const base = String(roleName || '').toLowerCase();
  if (base.includes('pre-manager') || base.includes('pre-gestor') || base.includes('pregestor')) {
    return 'pre-manager';
  }
  if (base.includes('coordinator') || base.includes('coordinador')) {
    return 'coordinator';
  }
  if (base.includes('manager') || base.includes('gestor')) {
    return 'manager';
  }
  if (
    base.includes('validator')
    || base.includes('verificator')
    || base.includes('verificador')
    || base.includes('validador')
  ) {
    return 'validator';
  }
  if (base.includes('editor')) {
    return 'editor';
  }
  return '';
};

const isRecordInPrePool = (record) => {
  if (!record || record.published_at) {
    return false;
  }
  const userId = record.current_user_id;
  if (userId == null || userId === '') {
    return true;
  }
  const numeric = Number(userId);
  return Number.isFinite(numeric) && numeric <= 0;
};

export const hasProspectoContent = (record = {}, process = '') => {
  const fields = PROSPECTO_SENTINEL_FIELDS[String(process || '').toLowerCase()] || [];
  return fields.some((field) => hasText(record[field]));
};

export const isDriDocumentPhase = (phase = '') =>
  phase === CASE_PHASE.DRI_PRELIMINAR || phase === CASE_PHASE.DRI_FINAL;

export const listPhaseFilterOptions = (values = []) => (
  values.map((value) => {
    const meta = PHASE_LABELS[value] || {};
    return {
      value,
      key: meta.key || `flow.case-phase.${value}`,
      fallback: meta.fallback || value
    };
  })
);

/** Gestor: en esta bandeja conviven DRI 2 y Prospecto 1. */
export const MANAGER_PHASE_FILTER_OPTIONS = listPhaseFilterOptions([
  CASE_PHASE.DRI_FINAL,
  CASE_PHASE.PROSPECTO
]);

/** Enlace / Coordinador / Panel / Listado: todas las fases. Barrido posterior. */
export const ALL_PHASE_FILTER_OPTIONS = listPhaseFilterOptions(Object.keys(PHASE_LABELS));

export const getCasePhaseMeta = (phase = '') =>
  PHASE_LABELS[phase] || PHASE_LABELS.dri_preliminar;

export const resolveCasePhase = (record = {}, options = {}) => {
  if (record?.case_phase && PHASE_LABELS[record.case_phase]) {
    return record.case_phase;
  }
  if (!record) {
    return CASE_PHASE.DRI_PRELIMINAR;
  }
  if (record.published_at) {
    return CASE_PHASE.PUBLICADO;
  }
  const roleKey = options.roleKey || normalizeRoleKey(options.roleName);
  const licenseCompleted = Boolean(options.licenseCompleted);
  const process = options.process || record.process || '';
  const prospecto = options.hasProspecto != null
    ? Boolean(options.hasProspecto)
    : hasProspectoContent(record, process);

  if (roleKey === 'editor') {
    return CASE_PHASE.BORRADOR_B;
  }
  if (roleKey === 'validator') {
    return licenseCompleted ? CASE_PHASE.BORRADOR_B : CASE_PHASE.BORRADOR_A;
  }
  if (roleKey === 'manager') {
    return prospecto ? CASE_PHASE.PROSPECTO : CASE_PHASE.DRI_FINAL;
  }
  if (roleKey === 'coordinator') {
    return isRecordInPrePool(record) ? CASE_PHASE.DRI_FINAL : CASE_PHASE.DRI_PRELIMINAR;
  }
  if (roleKey === 'pre-manager') {
    return isRecordInPrePool(record) ? CASE_PHASE.DRI_FINAL : CASE_PHASE.DRI_PRELIMINAR;
  }
  if (prospecto) {
    return CASE_PHASE.PROSPECTO;
  }
  return isRecordInPrePool(record) ? CASE_PHASE.DRI_FINAL : CASE_PHASE.DRI_PRELIMINAR;
};

export const eventTouchesProspecto = (event = {}, process = '') => {
  const diff = event.diff_snapshot;
  if (!diff || typeof diff !== 'object') {
    return false;
  }
  const fields = PROSPECTO_SENTINEL_FIELDS[String(process || '').toLowerCase()] || [];
  return fields.some((field) => {
    const cell = diff[field];
    if (!cell || typeof cell !== 'object') {
      return false;
    }
    return hasText(cell.next);
  });
};

export const annotateHistoryEventsWithPhase = (events = [], options = {}) => {
  const process = options.process || '';
  const roleNameById = options.roleNameById || {};
  const publishedStateIds = new Set((options.publishedStateIds || []).map(Number));
  const sorted = [...(events || [])].sort((a, b) => {
    const dateA = new Date(a.created_at || 0).getTime();
    const dateB = new Date(b.created_at || 0).getTime();
    if (dateA !== dateB) {
      return dateA - dateB;
    }
    return Number(a.id || 0) - Number(b.id || 0);
  });

  let prospecto = false;
  let licenseDone = Boolean(options.licenseCompleted);
  return sorted.map((event) => {
    prospecto = prospecto || eventTouchesProspecto(event, process);
    const ruleValue = String(event.rule?.value || event.RuleFlow_To_FlowHistory?.value || '').toLowerCase();
    if (ruleValue === 'end_license_verification') {
      licenseDone = true;
    }
    const roleName = roleNameById[Number(event.catalogrole_id)] || '';
    const published = publishedStateIds.has(Number(event.state_catalog_id));
    const phase = resolveCasePhase(
      {
        current_user_id: event.user_id,
        published_at: published ? event.created_at || true : null
      },
      {
        roleName,
        hasProspecto: prospecto,
        process,
        licenseCompleted: licenseDone
      }
    );
    const meta = getCasePhaseMeta(phase);
    return { ...event, case_phase: phase, case_phase_label: meta.fallback };
  });
};

export const resolveExcelRoleKeyForPhase = (phase, roleKey, fallbackRoleKey) => {
  const actor = String(roleKey || '').trim().toLowerCase();
  const current = String(phase || '').trim().toLowerCase();
  if (actor === 'pre-manager') {
    return 'pre-manager';
  }
  if (actor === 'validator' || actor === 'editor') {
    return actor;
  }
  if (actor === 'manager') {
    if (current === CASE_PHASE.DRI_PRELIMINAR || current === CASE_PHASE.DRI_FINAL) {
      return fallbackRoleKey || 'manager';
    }
    return fallbackRoleKey || 'manager';
  }
  return fallbackRoleKey;
};
